import { PlatformLocation } from '@angular/common';
import { HttpProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { HttpStatusCode } from '../constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents, PiiString } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise, InstrumentedHttpPromiseCallback } from '../instrumentation/instrumented-http.service';
import { ErrorDialogErrorCategory } from '../models/error-dialog.model';
import { FileItem, FileType } from '../models/file-item.model';
import { GraphError } from '../models/graph.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { GraphDriveItem, GraphThumbnailCollection, GraphUploadSession, GraphUploadSessionStatus, SiteInformation } from '../models/onedrive-graph.model';
import { OperationStream } from '../models/operation-stream.model';
import { MruFileItem } from '../mru/mru-file-item.model';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ContextService } from '../shared-context/context.service';
import { PromiseCancellationToken } from '../utilities/promise-cancellation-token.utility';
import { UrlBuilder } from '../utilities/url-builder.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { CacheFirstRequestService } from './cache-first-request.service';
import { DateService } from './date.service';
import { GraphService, ListItemVersion } from './graph.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { GetDownloadUrlProps, OneDriveAction, OneDriveRootUrls, OneDriveService } from './onedrive.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { VroomService } from './vroom.service';
import { XhrServiceError } from './xhr.service';

/**
 * Service that makes requests to OneDrive Graph apis.
 */
@Injectable({
    providedIn: 'root'
})
export class OneDriveGraphService extends GraphService implements OneDriveService {
    public static readonly myDriveEndpoint = 'me/drive/';
    public static readonly drivesEndpoint = 'drives/';
    public static readonly sitesEndpoint = 'sites/';
    public static readonly itemsEndpoint = 'items/';

    private myDriveRootUrlPromise: Promise<OneDriveRootUrls>;
    private myDriveRootUrlStream: OperationStream<OneDriveRootUrls>;
    private myDriveRootAuthErrorCached: boolean;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly dateService: DateService,
        private readonly vroomService: VroomService,
        scheduler: SchedulerService,
        private readonly cachedRequestService: CacheFirstRequestService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, contextService, officeHomeApiConfigService, platformLocation, globalErrorHandler, scheduler);

        // On reauth, the Graph token can be refreshed with new claims so these APIs might work.
        // Therefore, clear any cached errors.
        accessTokenService.reauthComplete.subscribe(() => {
            // TODO: consider proactively calling APIs again with new token.
            if (this.myDriveRootAuthErrorCached) {
                this.myDriveRootUrlStream = null;
                this.myDriveRootUrlPromise = null;
            }
        });
    }

    public getItemWithIds(
        driveId: string,
        itemId: string,
        properties: string,
        strategy: FetchStrategy,
        instrumentCallback?: InstrumentedHttpPromiseCallback<GraphDriveItem | GraphError>,
        beta = false,
        bypassMemoryCache = false
    ): Promise<GraphDriveItem> {
        const itemUrl = this.getItemLocation(driveId, itemId);
        const select = properties ? `?$select=${properties}` : '';
        const url = this.inMockMode ? `${itemUrl}?currentDate=${this.dateService.getCurrentDateMinuteGranularity().toISOString()}` : `${itemUrl}${select}`;
        return this.getOneDriveItem(
            KnownInstrumentationEvents.OneDriveGraphItemRequest,
            KnownRequestInstrumentationEvents.OneDriveGraphItemRequest,
            url,
            strategy,
            instrumentCallback,
            beta,
            null,
            bypassMemoryCache
        );
    }

    public getMyDriveRootUrl(schedulePriority = SchedulePriority.Immediate): Promise<OneDriveRootUrls> {
        if (!this.capabilitiesService.capabilities.oneDriveApisEnabled) {
            return Promise.resolve<OneDriveRootUrls>(null);
        }

        if (!this.myDriveRootUrlPromise) {
            const rootUrlPromise = new Promise<OneDriveRootUrls>((resolve, reject) => {
                const rootUrlStream = this.getMyDriveRootUrlStream(schedulePriority);
                rootUrlStream.results.pipe(take(1)).toPromise().then(url => {
                    if (!url || !url.userRoot) {
                        reject('No/Incomplete data returned.');
                        return;
                    }

                    resolve(url);
                });

                rootUrlStream.initialCompletion.then(error => {
                    if (!error) {
                        return;
                    }

                    this.globalErrorHandler.handleCacheObservableError(error, ErrorId.Evsnp, ErrorId.SiCWx);
                    if (error.cacheError && error.networkError) {
                        reject(error);
                    }
                });
            });

            this.myDriveRootAuthErrorCached = false;
            this.myDriveRootUrlPromise = rootUrlPromise;
        }

        return this.myDriveRootUrlPromise;
    }

    public getMyDriveRootUrlStream(schedulePriority = SchedulePriority.Immediate): OperationStream<OneDriveRootUrls> {
        if (this.myDriveRootUrlStream) {
            return this.myDriveRootUrlStream;
        }

        const rootUrlFromCache = () => this.getMyDriveRootUrlInternal(FetchStrategy.CacheOnly, schedulePriority);
        const rootUrlFromNetwork = () => this.getMyDriveRootUrlInternal(FetchStrategy.NetworkFirst, schedulePriority);
        const rootUrlStream = this.cachedRequestService.makeRequest(
            'my-drive-root',
            this.contextService.sharedContext.graphApiRootRefreshIntervalInSeconds,
            rootUrlFromCache,
            rootUrlFromNetwork,
            null,
            reason => {
                // Don't clear cached promise/stream on 401 errors. This is to prevent multiple retries that will all result in 401s,
                // creating additional load on SharePoint.
                // Note: 401 here means the internal xhr.service.ts got a 401, fetched a new token, retried, and still got 401.
                // We see Graph return 401 when a new user's identity hasn't synced across AAD and SPODS.
                // TODO: consider removing this in the future once identity syncing issues are resolved on the SharePoint side.
                //
                // Don't clear cached promise/stream on CAP errors since retries will not help. Only clear the cache if the user reauthenticates.
                if (reason && reason.httpResponse && (reason.httpResponse.status === 401 ||
                    (reason.httpResponse.status === HttpStatusCode.TokenUnavailable && reason.httpResponse.data === 'CAP_Error'))) {
                    this.myDriveRootAuthErrorCached = true;
                    return;
                }

                this.myDriveRootUrlStream = null;
                this.myDriveRootUrlPromise = null;
            });

        this.myDriveRootAuthErrorCached = false;
        this.myDriveRootUrlStream = rootUrlStream;
        return this.myDriveRootUrlStream;
    }

    public getSiteTitle(siteId: string): Promise<string> {
        const endpoint = `${OneDriveGraphService.sitesEndpoint}${siteId}`;
        // To prevent throttling issues, only make this request if no cached response is found
        // and rely on cache expiration to clear cached data
        return this.getGraph<SiteInformation>(
            KnownInstrumentationEvents.GetSiteTitleRequest,
            KnownRequestInstrumentationEvents.GetSiteTitleRequest,
            endpoint,
            FetchStrategy.CacheOnly,
            null,
            true,
            null,
            null,
            null,
            false,
            HttpResponseContentType.Json,
            SchedulePriority.Immediate
        )
        .then(result => result?.data?.displayName)
        .catch(() => this.getGraph<SiteInformation>(
            KnownInstrumentationEvents.GetSiteTitleRequest,
            KnownRequestInstrumentationEvents.GetSiteTitleRequest,
            endpoint,
            FetchStrategy.NetworkFirst,
            null,
            true,
            null,
            null,
            null,
            false,
            HttpResponseContentType.Json,
            SchedulePriority.Immediate
            )
            .then(result => result?.data?.displayName)
        );
    }

    public getFileListingVersions(url: string): Promise<Array<ListItemVersion>> {
        return this.getGraph<Array<ListItemVersion>>(
            KnownInstrumentationEvents.FileVersionListFetch,
            KnownRequestInstrumentationEvents.FileVersionListFetch,
            url,
            FetchStrategy.NetworkOnly
        ).then(result => result.data['value']);
    }

    public getSharedItemWithThumbnail(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getSharedItemWithParams(webUrl, documentType, fileFormat, VroomService.getItemParams(['id', 'sharepointIds', 'parentReference', 'createdDateTime'], fetchWacData, true), strategy, bypassMemoryCache, schedulePriority);
    }

    public getSharedItemWithoutThumbnail(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, action: OneDriveAction = 'open', bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getSharedItemWithParams(webUrl, documentType, fileFormat, VroomService.getItemParams(['id', 'sharepointIds', 'parentReference', 'createdDateTime'], fetchWacData, false, '', action), strategy, bypassMemoryCache, schedulePriority);
    }

    public getSharedItemWithThumbnailAndIds(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getSharedItemWithParams(webUrl, documentType, fileFormat, VroomService.getItemParams(['sharepointIds'], fetchWacData, true), strategy, bypassMemoryCache, schedulePriority);
    }

    public getItemWithWacDataAndThumbnails(item: MruFileItem, strategy: FetchStrategy, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        const itemLocation = this.getItemLocation(item.driveId, item.docId);
        const params = VroomService.getItemParams([], true, true);

        return this.getOneDriveItem(
            KnownInstrumentationEvents.OneDriveGraphItemWithWacDataAndThumbRequest,
            KnownRequestInstrumentationEvents.OneDriveGraphItemWithWacDataAndThumbRequest,
            `${itemLocation}?${params}`,
            strategy,
            null,
            null,
            schedulePriority,
            bypassMemoryCache);
    }

    public getItemWithWacData(_docUrl: string, driveId: string, docId: string, strategy: FetchStrategy, action: OneDriveAction = 'open', bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        const itemLocation = this.getItemLocation(driveId, docId);
        const params = VroomService.getItemParams([], true, false, '', action);

        return this.getOneDriveItem(
            KnownInstrumentationEvents.OneDriveGraphItemWithWacDataRequest,
            KnownRequestInstrumentationEvents.OneDriveGraphItemWithWacDataRequest,
            `${itemLocation}?${params}`,
            strategy,
            null,
            null,
            schedulePriority,
            bypassMemoryCache);
    }

    public getThumbnailsForItem(driveId: string, itemId: string, strategy: FetchStrategy, schedulePriority = SchedulePriority.Immediate): Promise<GraphThumbnailCollection> {
        const itemLocation = this.getItemLocation(driveId, itemId);
        const url = `${itemLocation}/thumbnails`;

        // To prevent throttling issues, only make this request if no cached response is found and rely on cache expiration to clear cached data
        if (strategy === FetchStrategy.NetworkFirst) {
            return this.getThumbnails(KnownInstrumentationEvents.OneDriveGraphThumbnailRequest, KnownRequestInstrumentationEvents.OneDriveGraphThumbnailRequest, url, FetchStrategy.CacheOnly, schedulePriority)
                .catch(() => this.getThumbnails(KnownInstrumentationEvents.OneDriveGraphThumbnailRequest, KnownRequestInstrumentationEvents.OneDriveGraphThumbnailRequest, url, FetchStrategy.NetworkFirst, schedulePriority));
        } else {
            return this.getThumbnails(KnownInstrumentationEvents.OneDriveGraphThumbnailRequest, KnownRequestInstrumentationEvents.OneDriveGraphThumbnailRequest, url, strategy, schedulePriority);
        }
    }

    public getThumbnailString(url: string): Promise<string> {
        const reader = new FileReader();

        return new Promise<string>((resolve, reject) => {
            this.getGraph<Blob>(
                KnownInstrumentationEvents.OneDriveGraphThumbnailStringRequest,
                KnownRequestInstrumentationEvents.OneDriveGraphThumbnailStringRequest,
                url,
                FetchStrategy.NetworkOnly,
                (result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                },
                true,
                null,
                null,
                null,
                false,
                HttpResponseContentType.Blob,
                SchedulePriority.Immediate
            ).then(data => {
                // TODO: remove once errors are figured out
                if (data && data.status && data.status === 406) {
                    const params: InstrumentationParams = {};
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(data);
                    params[InstrumentationParam.HttpCode] = data.status.toString();
                    params[InstrumentationParam.DebugInfo] = (data.statusText === null || typeof data.statusText === 'undefined') ? '' : data.statusText;
                    this.instrumentationService.logDebugCacheEvent(KnownInstrumentationEvents.OneDriveGraphThumbnailStringRequest, params);
                }
                if (data && data.data) {
                    reader.onloadend = () => {
                        const result = (reader.result && typeof reader.result === 'string' && reader.result) || '';
                        if (result.length > 0) {
                            resolve(result);
                        } else {
                            reject();
                        }
                    };
                    reader.onerror = reject;
                    reader.onabort = reject;
                    reader.readAsDataURL(data.data);
                } else {
                    reject();
                }
            }).catch(error => {
                this.globalErrorHandler.handleError(error, ErrorId.AfEUN);
                reject();
            });
        });
    }

    public createNewFile(filename: string, fileType: FileType, fetchOpenWithData = false): Promise<GraphDriveItem> {
        const url = `${OneDriveGraphService.myDriveEndpoint}root/children?select=webDavUrl,${fetchOpenWithData ? 'openwith,' : ''}*`;
        const postBody = {
            'name': filename,
            'file': {},
            '@microsoft.graph.conflictBehavior': 'rename'
        };

        // Special header Prefer:allow-zerobyte is required for MSA account to create files
        const specialHeaders = new Map([
            ['accept', 'application/json'],
            ['Prefer', 'allow-zerobyte'],
            ['application', 'officehome']
        ]);

        return new Promise<GraphDriveItem>((resolve, reject) => {
            this.postGraph<GraphDriveItem | GraphError>(
                KnownInstrumentationEvents.OneDriveGraphCreateFileRequest,
                KnownRequestInstrumentationEvents.OneDriveGraphCreateFileRequest,
                url,
                postBody,
                (promise: InstrumentedHttpPromise<GraphDriveItem | GraphError>) => {
                    promise
                        .instrument((result, params) => {
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                            if (result.ok) {
                                const item = result.data as GraphDriveItem;
                                if (!item || !item.id || !item.webUrl) {
                                    params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                                }
                            }
                        })
                        .then(response => {
                            const item = response.data as GraphDriveItem;
                            if (item && item.id && item.webUrl) {
                                resolve(item);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse drive item from Graph response'));
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.MCFQq);
                        });
                },
                (xhrError: XhrServiceError<GraphDriveItem | GraphError>) => {
                    if (xhrError && xhrError.httpResponse) {
                        if (xhrError.httpResponse.data) {
                            reject({ error: { code: `${xhrError.httpResponse.status}`, data: `${xhrError.httpResponse.data}`} });
                        } else {
                            reject({ error: { code: `${xhrError.httpResponse.status}` } });
                        }
                    } else {
                        reject();
                    }
                },
                null,
                null,
                null,
                null,
                false,
                true,
                { category: ErrorDialogErrorCategory.CreateFile, fileType },
                specialHeaders);
        });
    }

    /**
     * Rename a file located in Sharepoint or OneDrive for Business
     * @param filename - new name of the file
     * @param itemId - itemId of the file to be renamed
     * @param driveId - optional parameter - will be set if file is located in Sharepoint
     */
    public renameFile(filename: string, itemId: string, driveId?: string): Promise<GraphDriveItem> {
        let endpoint;
        if (!!driveId) {
            // This is a Sharepoint location
            endpoint = this.getItemLocation(driveId, itemId);
        } else {
            // This is a OneDrive for Business location
            endpoint = `${OneDriveGraphService.myDriveEndpoint}items/${itemId}`;
        }

        const requestBody = {
            name: filename
        };

        const renameFileRequestConfig = this.getRequestConfig();
        renameFileRequestConfig.headers.set('Content-Type', 'application/json');

        return new Promise ((resolve, reject) => {
            this.patchGraph<GraphDriveItem | GraphError>(
                KnownInstrumentationEvents.DocumentTitleRenameFile,
                KnownRequestInstrumentationEvents.DocumentTitleRenameFile,
                endpoint,
                JSON.stringify(requestBody),
                (promise: InstrumentedHttpPromise<GraphDriveItem | GraphError>) => {
                    promise.instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        if (result.ok) {
                            const item = result.data as GraphDriveItem;
                            if (!item || !item.id || !item.webUrl) {
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                        }
                    }).then(response => {
                        const item = response.data as GraphDriveItem;
                            if (item && item.id && item.webUrl) {
                                resolve(item);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse drive item from Graph response'));
                            }
                    });
                },
                (xhrError: XhrServiceError<GraphDriveItem | GraphError>) => {
                    if (xhrError && xhrError.httpResponse) {
                        if (xhrError.httpResponse.data) {
                            reject({ error: { code: `${xhrError.httpResponse.status}`, data: `${xhrError.httpResponse.data}`} });
                        } else {
                            reject({ error: { code: `${xhrError.httpResponse.status}` } });
                        }
                    } else {
                        reject();
                    }
                },
                renameFileRequestConfig,
                null,
                null,
                null,
                null,
                undefined,
                null);
        });
    }

    public getDownloadUrl(getDownloadUrlProps: GetDownloadUrlProps): Promise<string> {
        if (!getDownloadUrlProps || !getDownloadUrlProps.itemId || !getDownloadUrlProps.driveId) {
            return Promise.reject('Invalid params');
        }

        return this.getItemWithIds(getDownloadUrlProps.driveId, getDownloadUrlProps.itemId, 'id,content.downloadUrl', FetchStrategy.NetworkOnly).then(driveItem => {
                if (!driveItem['@microsoft.graph.downloadUrl']) {
                    return Promise.reject('Missing downloadUrl');
                }

                return UrlBuilder.removeQueryParams(driveItem['@microsoft.graph.downloadUrl'], ['tempAuth', 'ApiVersion'], false);
            });
    }

    // Uploads an entire file to OneDrive
    public uploadFile(
        file: File,
        sessionId: string,
        parent?: string,
        path?: string,
        onProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number
    ): Promise<GraphDriveItem> {
        return new Promise<GraphDriveItem>((resolve, reject) => {
            if (!file || !file.name) {
                reject(new XhrServiceError(null, 'Invalid file'));
            }

            const url = `${this.vroomService.getUploadLocation(OneDriveGraphService.myDriveEndpoint, file, parent, path)}content?@microsoft.graph.conflictBehavior=rename&select=webDavUrl,*`;
            this.putGraph<GraphDriveItem | GraphError>(
                KnownInstrumentationEvents.OneDriveGraphUploadFileRequest,
                KnownRequestInstrumentationEvents.OneDriveGraphUploadFileRequest,
                url,
                file,
                promise => {
                    promise
                        .instrument((result, params) => this.vroomService.instrumentUploadResponse(result, params, file, sessionId))
                        .then(response => {
                            const item = response.data as GraphDriveItem;
                            if (item && item.id) {
                                resolve(item);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse drive item from Graph response'));
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.NWgrO);
                        });
                },
                reject,
                onProgress,
                null,
                cancellationToken,
                timeoutInMs);
        });
    }

    // PWA TODO: Need to support canonical URLs for opening large files after uploading
    // Creates a new upload session for the requested file
    public createUploadSession(file: File, sessionId: string, parent?: string, path?: string, cancellationToken?: PromiseCancellationToken): Promise<GraphUploadSession> {
        return new Promise<GraphUploadSession>((resolve, reject) => {
            if (!file || !file.name) {
                reject(new XhrServiceError(null, 'Invalid file'));
            }
            const url = `${this.vroomService.getUploadLocation(OneDriveGraphService.myDriveEndpoint, file, parent, path)}createUploadSession`;
            const postBody = {
                item: {
                    '@microsoft.graph.conflictBehavior': 'rename'
                }
            };

            this.postGraph<GraphUploadSession | GraphError>(
                KnownInstrumentationEvents.OneDriveGraphCreateUploadSessionRequest,
                KnownRequestInstrumentationEvents.OneDriveGraphCreateUploadSessionRequest,
                url,
                postBody,
                promise => {
                    promise
                        .instrument((result, params) => {
                            this.vroomService.instrumentUploadResponse(result, params, file, sessionId);
                            if (result.ok) {
                                const session = result.data as GraphUploadSession;
                                params[InstrumentationParam.Length] = (session && session.uploadUrl) ? session.uploadUrl.length.toString() : '0';
                            }
                        })
                        .then(response => {
                            const session = response.data as GraphUploadSession;
                            if (session && session.uploadUrl) {
                                resolve(session);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse upload session from Graph response'));
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.Nt3wB);
                        });
                },
                reject,
                null,
                null,
                cancellationToken
            );
        });
    }

    // Cancels an existing upload session
    public cancelUploadSession(sessionUrl: string, sessionId: string, cancellationToken?: PromiseCancellationToken): Promise<void> {
        return this.vroomService.cancelUploadSessionInternal(sessionUrl, sessionId, cancellationToken, AccessTokenConsumer.Graph);
    }

    // Uploads a file fragment to an existing upload session
    // Note: startByte is the index of the first byte to be sent, while endByte is the first index not included in the fragment (ex. first 10 byte fragment would be (0, 10))
    public uploadFragment(
        sessionUrl: string,
        startByte: number,
        endByte: number,
        fileSize: number,
        content: Blob,
        sessionId: string,
        onProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeout?: number
    ): Promise<GraphUploadSessionStatus | GraphDriveItem> {
        return this.vroomService.uploadFragment(sessionUrl, startByte, endByte, fileSize, content, sessionId, onProgress, cancellationToken, timeout);
    }

    //TODO: Enable manual commits of in-progress upload sessions once the API onboards to graph

    // Retrieves the status of an existing upload session
    public getUploadSessionStatus(sessionUrl: string, sessionId: string, cancellationToken: PromiseCancellationToken): Promise<GraphDriveItem | GraphUploadSessionStatus> {
        return this.vroomService.getUploadSessionStatusInternal(sessionUrl, sessionId, cancellationToken, AccessTokenConsumer.Graph);
    }

    /**
     * Returns a promise with OneDrive information as GraphDriveItem
     */
    public getMyDriveRoot(strategy: FetchStrategy, schedulePriority: SchedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getMyDriveItem('root', strategy, schedulePriority);
    }

    protected isRetriableError(status: number) {
        return status === HttpStatusCode.Throttled;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        super.handleQueryParam(paramNameInLowerCase, value, requestConfig);
        if (paramNameInLowerCase === 'mockonedrive' && this.mockRequestHeader) {
            requestConfig?.headers.set(this.mockRequestHeader, value);
        }
    }

    private getSharedItemWithParams(webUrl: string, documentType: FileType, fileFormat: string, params: string, strategy: FetchStrategy, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return new Promise<GraphDriveItem>((resolve, reject) => {
            const shareUrl = this.vroomService.getSharesUrl(webUrl);
            if (!shareUrl) {
                reject(new XhrServiceError(null, 'Unable to parse share id'));
                return;
            }

            const endpoint = this.inMockMode
                ? `shares?itemUrl=${encodeURIComponent(shareUrl)}&currentDate=${this.dateService.getCurrentDateMinuteGranularity().toISOString()}`
                : `${shareUrl}${params ? '?' + params : ''}`;

            const isWacDataRequest = params.includes('openwith');

            this.getOneDriveItem(
                isWacDataRequest ? KnownInstrumentationEvents.OneDriveGraphSharesWithWacDataRequest : KnownInstrumentationEvents.OneDriveGraphSharesRequest,
                isWacDataRequest ? KnownRequestInstrumentationEvents.OneDriveGraphSharesWithWacDataRequest : KnownRequestInstrumentationEvents.OneDriveGraphSharesRequest,
                endpoint,
                strategy,
                (result, instrumentationParams) => {
                    if (result.ok) {
                        const item = result.data as GraphDriveItem;
                        const thumbnails = item ? item.thumbnails : null;
                        instrumentationParams[InstrumentationParam.ItemCount] = (thumbnails ? thumbnails.length : 0).toString();
                    }
                    const parsedUrl = UrlParser.getLocation(webUrl);
                    if (parsedUrl && parsedUrl.protocol && parsedUrl.host) {
                        const resource = `${parsedUrl.protocol}//${parsedUrl.host}`;
                        instrumentationParams[InstrumentationParam.ResourceUrl] = new PiiString(resource);
                        instrumentationParams[InstrumentationParam.ItemIsShared] = (!(resource === this.contextService.sharedContext.sharePointResourceId || resource === this.contextService.sharedContext.oneDriveResourceId)).toString();
                    }
                    instrumentationParams[InstrumentationParam.ItemCategory] = FileItem.referralFromFileLocation(webUrl);
                    instrumentationParams[InstrumentationParam.DocumentType] = FileType[documentType];
                    if (fileFormat) {
                        instrumentationParams[InstrumentationParam.FileFormat] = fileFormat;
                    }
                },
                false,
                schedulePriority,
                bypassMemoryCache
            ).then(resolve, reject);
        });
    }

    private getItemLocation(driveId: string, itemId: string): string {
        return `${OneDriveGraphService.drivesEndpoint}${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}`;
    }

    private getMyDriveItem(url: string, strategy: FetchStrategy, schedulePriority: SchedulePriority): Promise<GraphDriveItem> {
        return this.getOneDriveItem(KnownInstrumentationEvents.OneDriveGraphItemRequest, KnownRequestInstrumentationEvents.OneDriveGraphItemRequest, `${OneDriveGraphService.myDriveEndpoint}${url}`, strategy, null, false, schedulePriority);
    }

    private getOneDriveItem(
        requestName: KnownInstrumentationEvents,
        otelRequestName: KnownRequestInstrumentationEvents,
        url: string,
        strategy: FetchStrategy,
        instrumentCallback?: InstrumentedHttpPromiseCallback<GraphDriveItem | GraphError>,
        beta = false,
        schedulePriority = SchedulePriority.Immediate,
        bypassMemoryCache = false
    ) {
        return this.getGraph<GraphDriveItem>(
            requestName,
            otelRequestName,
            url,
            strategy,
            (result, event) => {
                if (instrumentCallback) {
                    instrumentCallback(result, event);
                }
            },
            true,
            null,
            null,
            null,
            beta,
            HttpResponseContentType.Json,
            schedulePriority,
            bypassMemoryCache
        ).then(result => result.data);
    }

    private getThumbnails(requestName: KnownInstrumentationEvents, otelRequestName: KnownRequestInstrumentationEvents, url: string, strategy: FetchStrategy, schedulePriority = SchedulePriority.Immediate): Promise<GraphThumbnailCollection> {
        const endpoint = this.inMockMode ? `thumbnails?itemUrl=${encodeURIComponent(url)}` : url;
        return this.getGraph<GraphThumbnailCollection>(
            requestName,
            otelRequestName,
            endpoint,
            strategy,
            (result, params) => {
                if (result.ok) {
                    const thumbnails = result.data as GraphThumbnailCollection;
                    const itemCount = ((thumbnails && thumbnails.value) ? thumbnails.value.length : 0).toString();
                    params[InstrumentationParam.ItemCount] = itemCount;
                }
            },
            true,
            null,
            null,
            null,
            false,
            HttpResponseContentType.Json,
            schedulePriority
        ).then(result => result.data);
    }

    private getMyDriveRootUrlInternal(strategy: FetchStrategy, schedulePriority: SchedulePriority): Promise<OneDriveRootUrls> {
        return this.getMyDriveRoot(strategy, schedulePriority).then(rootItem =>
            this.vroomService.getRootUrlsFromRootItem(rootItem, strategy));
    }
}
